/**
 * BookingChatHistory - Shows chat messages for a single booking
 * Staff view of customer-talent chat threads. With canDelete (Admin, Talent Coordinator; Traci, 27 Oct 2026) each
 * message and each conversation has Delete: the messages are hidden everywhere and kept in the database
 * (POST /api/admin/staging/bookings/:id/chat/delete).
 */

import { useState, useEffect, useCallback } from 'react'
import { MessageCircle, Loader2, User, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabaseAdmin } from '../../integrations/supabase/client'
import { stagingFetch } from '@/staging/api'
import { format } from 'date-fns'

interface Message {
  id: string
  sender_id: string
  message_text: string
  created_at: string
  attachment_url?: string
  attachment_name?: string
  thread_id?: string | null
  sender: {
    full_name: string
  }
}

interface ChatThread {
  id: string
  talent_role: 'photographer' | 'videographer' | 'second_photographer'
  last_message_at?: string | null
  /** Set when this talent was replaced on the role (26 Oct 2026): kept as the record, read-only */
  ended_at?: string | null
  talent: {
    full_name: string
  } | null
}

interface BookingChatHistoryProps {
  bookingId: string
  /** One tab per talent thread (the new console's View Chat pop-up, prototype v108) instead of every thread stacked. */
  tabs?: boolean
  /** Admin and Talent Coordinator: Delete on each message and each conversation */
  canDelete?: boolean
}

const roleLabel = (role: string) => {
  if (role === 'videographer') return 'Videographer'
  if (role === 'second_photographer') return 'Second Photographer'
  return 'Photographer'
}

export default function BookingChatHistory({ bookingId, tabs = false, canDelete = false }: BookingChatHistoryProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [tab, setTab] = useState<string | null>(null)
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [isLoading, setIsLoading] = useState(true)
  // The Delete waiting for its confirm: one message id, or 'all:<thread id | legacy>' for a whole conversation
  const [confirming, setConfirming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fetchChatHistory = useCallback(async () => {
    try {
      const { data: threadData, error: threadError } = await supabaseAdmin
        .from('booking_chat_threads')
        // *: ended_at once the 26 Oct 2026 migration has run, without breaking before it
        .select(`
          *,
          talent:users!booking_chat_threads_talent_user_id_fkey(full_name)
        `)
        .eq('booking_id', bookingId)
        .order('talent_role', { ascending: true })

      if (threadError) {
        console.warn('Booking chat threads unavailable, falling back to booking-level history:', threadError)
      } else {
        // Current conversations first, then the ones a replaced talent left
        setThreads(((threadData || []) as ChatThread[]).sort((a, b) => Number(Boolean(a.ended_at)) - Number(Boolean(b.ended_at))))
      }

      const { data: messageData, error: messageError } = await supabaseAdmin
        .from('booking_messages')
        .select(`
          id,
          sender_id,
          thread_id,
          message_text,
          created_at,
          attachment_url,
          attachment_name,
          sender:users!sender_id(full_name)
        `)
        .eq('booking_id', bookingId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })

      if (messageError) {
        console.error('Error fetching booking messages:', messageError)
        throw messageError
      }

      console.log(`📨 Fetched ${messageData?.length || 0} messages for booking ${bookingId}`)
      setMessages(messageData || [])
    } catch (error) {
      console.error('Error fetching messages:', error)
    } finally {
      setIsLoading(false)
    }
  }, [bookingId])

  useEffect(() => { fetchChatHistory() }, [fetchChatHistory])

  const deleteMessages = async (ids: string[]) => {
    if (!ids.length) return
    setDeleting(true)
    try {
      const r = await stagingFetch<{ deleted: number }>(`/api/admin/staging/bookings/${bookingId}/chat/delete`, { method: 'POST', body: JSON.stringify({ message_ids: ids }) })
      toast.success(r.deleted === 1 ? 'Message deleted' : `${r.deleted} messages deleted`)
      setConfirming(null)
      await fetchChatHistory()
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete')
    } finally {
      setDeleting(false)
    }
  }

  // Inline "Delete? Delete / Cancel" so nothing goes on one click
  const deleteControl = (key: string, ids: string[], label: string) => {
    if (!canDelete || !ids.length) return null
    if (confirming !== key) {
      return (
        <button type="button" onClick={() => setConfirming(key)} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-600">
          <Trash2 className="w-3 h-3" /> {label}
        </button>
      )
    }
    return (
      <span className="inline-flex flex-wrap items-center gap-2 text-xs">
        <span className="text-red-700">{ids.length > 1 ? `Delete ${ids.length} messages for the client and talent?` : 'Delete for the client and talent?'}</span>
        <button type="button" disabled={deleting} onClick={() => deleteMessages(ids)} className="font-medium text-red-700 hover:underline disabled:opacity-50">{deleting ? 'Deleting...' : 'Yes, delete'}</button>
        <button type="button" disabled={deleting} onClick={() => setConfirming(null)} className="text-gray-500 hover:underline">Cancel</button>
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <MessageCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">No chat messages yet</p>
      </div>
    )
  }

  const messagesByThread = threads.map((thread) => ({
    thread,
    messages: messages.filter((message) => message.thread_id === thread.id)
  }))

  const legacyMessages = messages.filter((message) => !message.thread_id)

  const renderMessages = (threadMessages: Message[]) => (
    <div className="space-y-3">
      {threadMessages.map((message) => (
        <div key={message.id} className="flex flex-col">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-medium text-sm text-gray-900">
              {message.sender?.full_name}
            </span>
            <span className="text-xs text-gray-400">
              {format(new Date(message.created_at), 'MMM d, h:mm a')}
            </span>
            <span className="ml-auto">{deleteControl(message.id, [message.id], 'Delete')}</span>
          </div>
          <div className="bg-white rounded-lg px-3 py-2 text-sm text-gray-700 border border-gray-200">
            {message.message_text}
          </div>
          {message.attachment_url && (
            <a
              href={message.attachment_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline mt-1"
            >
              📎 {message.attachment_name || 'Attachment'}
            </a>
          )}
        </div>
      ))}
    </div>
  )

  const deleteAll = (key: string, list: Message[]) => deleteControl(`all:${key}`, list.map((m) => m.id), 'Delete conversation')

  if (threads.length === 0) {
    return (
      <div className="space-y-3 max-h-96 overflow-y-auto bg-gray-50 rounded-lg p-4">
        {canDelete && <div className="flex justify-end">{deleteAll('booking', messages)}</div>}
        {renderMessages(messages)}
      </div>
    )
  }

  if (tabs) {
    const keys = [...messagesByThread.map(({ thread }) => thread.id), ...(legacyMessages.length ? ['legacy'] : [])]
    const current = tab && keys.includes(tab) ? tab : keys[0]
    const shown = current === 'legacy' ? legacyMessages : messagesByThread.find(({ thread }) => thread.id === current)?.messages || []
    return (
      <div>
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Talent threads">
          {messagesByThread.map(({ thread, messages: m }) => (
            <button key={thread.id} type="button" role="tab" aria-selected={current === thread.id} onClick={() => setTab(thread.id)} className={`rounded-full border px-3 py-1 text-xs ${current === thread.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>
              {thread.talent?.full_name || 'Assigned Talent'} · {roleLabel(thread.talent_role)}{thread.ended_at ? ` · previous, ended ${format(new Date(thread.ended_at), 'MMM d')}` : ''} ({m.length})
            </button>
          ))}
          {legacyMessages.length > 0 && <button type="button" role="tab" aria-selected={current === 'legacy'} onClick={() => setTab('legacy')} className={`rounded-full border px-3 py-1 text-xs ${current === 'legacy' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>Earlier chat ({legacyMessages.length})</button>}
        </div>
        {canDelete && shown.length > 0 && <div className="mt-2 flex justify-end">{deleteAll(current, shown)}</div>}
        <div className="mt-3 max-h-[28rem] overflow-y-auto rounded-lg bg-gray-50 p-4">
          {shown.length === 0 ? <p className="text-sm text-gray-500">No messages in this thread yet.</p> : renderMessages(shown)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 max-h-[32rem] overflow-y-auto bg-gray-50 rounded-lg p-4">
      {messagesByThread.map(({ thread, messages: threadMessages }) => (
        <div key={thread.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {thread.talent?.full_name || 'Assigned Talent'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {roleLabel(thread.talent_role)}
                    {thread.ended_at && <> · previous talent, ended {format(new Date(thread.ended_at), 'MMM d')} (read-only record)</>}
                  </p>
                </div>
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0 flex flex-col items-end gap-1">
                {threadMessages.length} message{threadMessages.length === 1 ? '' : 's'}
                {deleteAll(thread.id, threadMessages)}
              </span>
            </div>
          </div>
          <div className="p-4">
            {threadMessages.length === 0 ? (
              <p className="text-sm text-gray-500">No messages in this thread yet.</p>
            ) : (
              renderMessages(threadMessages)
            )}
          </div>
        </div>
      ))}

      {legacyMessages.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-amber-200">
            <p className="text-sm font-semibold text-amber-900">Legacy Booking Chat</p>
            <p className="text-xs text-amber-700">Messages without a thread assignment</p>
            {canDelete && <div className="mt-1">{deleteAll('legacy', legacyMessages)}</div>}
          </div>
          <div className="p-4">
            {renderMessages(legacyMessages)}
          </div>
        </div>
      )}
    </div>
  )
}
